import type { CardDto } from '@kanban/shared';
import { useSortable } from '@dnd-kit/sortable';
import { CardTile } from '../CardTile.js';

/**
 * Karte als Ziehpunkt.
 *
 * Bewusst OHNE das `transform` von `useSortable`: die Originalkarte bleibt an
 * ihrer Stelle stehen und wird nur blass (Ghost, SPEC 8.2.2). Die Bewegung
 * zeigt der DragOverlay, die Zielposition der eingeschobene Platzhalter.
 * `useSortable` liefert hier nur Kollisionserkennung und Tastaturbedienung.
 *
 * Tastaturbelegung (SPEC 8.2): Leertaste nimmt auf und legt ab, Pfeiltasten
 * bewegen, Escape bricht ab. Enter bleibt frei und oeffnet das Kartendetail —
 * deshalb ist der KeyboardSensor auf die Leertaste allein eingestellt.
 */
export function SortableCard({
  card,
  isDragging,
  onOpen,
  watcherName = null,
  onComplete,
}: {
  card: CardDto;
  isDragging: boolean;
  onOpen: (card: CardDto) => void;
  watcherName?: string | null;
  onComplete?: (card: CardDto) => void;
}) {
  const { attributes, listeners, setNodeRef } = useSortable({
    id: card.id,
    data: { type: 'card', columnId: card.columnId },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onOpen(card);
          return;
        }
        listeners?.onKeyDown?.(event);
      }}
      aria-label={`Karte ${card.title}`}
      data-testid="sortable-card"
      data-card-id={card.id}
      style={{
        opacity: isDragging ? 0.4 : 1,
        // Nicht `none`: vor dem Long-Press muss die Spalte noch scrollen
        // koennen. Nach dem Aufnehmen unterdrueckt dnd-kit das Scrollen selbst.
        touchAction: 'manipulation',
        cursor: 'grab',
      }}
    >
      <CardTile card={card} onOpen={onOpen} watcherName={watcherName} onComplete={onComplete} />
    </div>
  );
}
